import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { WaitlistForm } from '../components/WaitlistForm';
import { Check, X, ArrowLeft, Shield, WifiOff, CreditCard, Zap } from 'lucide-react';

const comparisonData = {
  adobe: {
    name: 'Adobe Acrobat Pro',
    price: '23.99\u20ac/month',
    annual: '~287\u20ac/year',
    features: [
      { label: 'One-time payment', hexo: true, competitor: false },
      { label: 'Text extraction (OCR)', hexo: true, competitor: true },
      { label: 'Works fully offline', hexo: true, competitor: false },
      { label: 'Privacy-first (no cloud)', hexo: true, competitor: false },
      { label: 'No account required', hexo: true, competitor: false },
      { label: 'PDF editing (merge, split, rotate)', hexo: true, competitor: true },
      { label: 'Annotations & signatures', hexo: true, competitor: true },
      { label: 'Image to PDF conversion', hexo: true, competitor: true },
      { label: 'Cloud storage', hexo: false, competitor: true },
      { label: 'e-Signatures (legal)', hexo: false, competitor: true },
      { label: 'Multi-platform (Mac, Linux)', hexo: false, competitor: true },
    ],
  },
  foxit: {
    name: 'Foxit PDF Editor',
    price: '17.99\u20ac/month',
    annual: '~215\u20ac/year',
    features: [
      { label: 'One-time payment', hexo: true, competitor: false },
      { label: 'Text extraction (OCR)', hexo: true, competitor: true },
      { label: 'Works fully offline', hexo: true, competitor: false },
      { label: 'Privacy-first (no cloud)', hexo: true, competitor: false },
      { label: 'No account required', hexo: true, competitor: false },
      { label: 'PDF editing (merge, split, rotate)', hexo: true, competitor: true },
      { label: 'Annotations & signatures', hexo: true, competitor: true },
      { label: 'Image to PDF conversion', hexo: true, competitor: true },
      { label: 'Cloud collaboration', hexo: false, competitor: true },
      { label: 'Advanced form creation', hexo: false, competitor: true },
      { label: 'Multi-platform (Mac, Linux)', hexo: false, competitor: true },
    ],
  },
};

const labelsI18n = {
  en: {
    heroTitle: (name) => `HexoPDF vs ${name}`,
    heroSubtitle: (name) => `See how HexoPDF compares to ${name}. No subscription. No cloud. Just your PDFs.`,
    hexoPrice: 'From 4.99\u20ac (one-time)',
    competitorPrice: (p) => p,
    feature: 'Feature',
    fiveYear: '5-year cost comparison',
    fiveYearHexo: '4.99\u20ac',
    back: 'Back to HexoPDF',
    ctaTitle: 'Ready to switch?',
    ctaText: 'Join the waitlist and get early access at the lowest price.',
  },
  fr: {
    heroTitle: (name) => `HexoPDF vs ${name}`,
    heroSubtitle: (name) => `D\u00e9couvrez comment HexoPDF se compare \u00e0 ${name}. Sans abonnement. Sans cloud. Juste vos PDF.`,
    hexoPrice: '\u00c0 partir de 4,99\u20ac (unique)',
    competitorPrice: (p) => p,
    feature: 'Fonctionnalit\u00e9',
    fiveYear: 'Co\u00fbt sur 5 ans',
    fiveYearHexo: '4,99\u20ac',
    back: 'Retour \u00e0 HexoPDF',
    ctaTitle: 'Pr\u00eat \u00e0 changer\u202f?',
    ctaText: 'Rejoignez la liste d\u2019attente et obtenez un acc\u00e8s anticip\u00e9 au meilleur prix.',
  },
  hi: {
    heroTitle: (name) => `HexoPDF vs ${name}`,
    heroSubtitle: (name) => `देखें कि HexoPDF ${name} से कैसे तुलना करता है। कोई सदस्यता नहीं। कोई क्लाउड नहीं। बस आपके PDF।`,
    hexoPrice: '4.99€ से (एकमुश्त)',
    competitorPrice: (p) => p,
    feature: 'सुविधा',
    fiveYear: '5 वर्षों की लागत तुलना',
    fiveYearHexo: '4.99€',
    back: 'HexoPDF पर वापस जाएं',
    ctaTitle: 'बदलने के लिए तैयार हैं?',
    ctaText: 'प्रतीक्षा सूची में शामिल हों और सबसे कम कीमत पर अर्ली एक्सेस प्राप्त करें।',
  },
};

export default function ComparisonPage({ competitor = 'adobe' }) {
  const { lang } = useI18n();
  const labels = labelsI18n[lang] || labelsI18n.en;
  const data = comparisonData[competitor];

  if (!data) return null;

  const annualCost = competitor === 'adobe' ? 287 : 215;
  const fiveYearCost = annualCost * 5;

  return (
    <div className="min-h-screen bg-background" data-testid={`comparison-page-${competitor}`}>
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-8 transition-colors" data-testid="comparison-back">
            <ArrowLeft className="w-4 h-4" />
            {labels.back}
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Hero */}
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground tracking-tight mb-4" data-testid="comparison-hero-title">
              {labels.heroTitle(data.name)}
            </h1>
            <p className="text-muted-foreground text-lg mb-12 max-w-2xl" data-testid="comparison-hero-subtitle">
              {labels.heroSubtitle(data.name)}
            </p>

            {/* Price highlight */}
            <div className="grid md:grid-cols-2 gap-4 mb-12">
              <div className="p-6 rounded-2xl bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-medium">HexoPDF</span>
                </div>
                <p className="font-heading text-3xl font-bold text-foreground">{labels.hexoPrice}</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground font-medium">{data.name}</span>
                </div>
                <p className="font-heading text-3xl font-bold text-muted-foreground">{labels.competitorPrice(data.price)}</p>
                <p className="text-muted-foreground/60 text-sm mt-1">{data.annual}</p>
              </div>
            </div>

            {/* Feature table */}
            <div className="rounded-2xl bg-card/30 border border-border overflow-hidden mb-12">
              <table className="w-full" data-testid="comparison-feature-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-6 text-sm text-muted-foreground font-normal">{labels.feature}</th>
                    <th className="py-4 px-4 text-center text-sm text-blue-400 font-medium">HexoPDF</th>
                    <th className="py-4 px-4 text-center text-sm text-muted-foreground font-medium">{data.name}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.features.map((f, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-3.5 px-6 text-sm text-foreground/80">{f.label}</td>
                      <td className="py-3.5 px-4 text-center">
                        {f.hexo ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> : <X className="w-4 h-4 text-muted-foreground/60 mx-auto" />}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        {f.competitor ? <Check className="w-4 h-4 text-muted-foreground mx-auto" /> : <X className="w-4 h-4 text-muted-foreground/60 mx-auto" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 5-year comparison */}
            <div className="p-8 rounded-2xl bg-card border border-border mb-12">
              <h3 className="font-heading text-xl font-semibold text-foreground mb-6">{labels.fiveYear}</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-blue-400">HexoPDF</span>
                    <span className="text-foreground font-medium">{labels.fiveYearHexo}</span>
                  </div>
                  <div className="h-3 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-blue-500" style={{ width: '1%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">{data.name}</span>
                    <span className="text-red-400 font-medium">{fiveYearCost.toLocaleString()}{'\u20ac'}</span>
                  </div>
                  <div className="h-3 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-red-500" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Key advantages */}
            <div className="grid md:grid-cols-3 gap-4 mb-16">
              {[
                { icon: CreditCard, title: lang === 'fr' ? 'Paiement unique' : 'One-time payment', desc: lang === 'fr' ? 'Pas d\u2019abonnement, jamais' : 'No subscription, ever' },
                { icon: WifiOff, title: lang === 'fr' ? '100% hors ligne' : '100% offline', desc: lang === 'fr' ? 'Aucune donn\u00e9e envoy\u00e9e' : 'No data sent anywhere' },
                { icon: Shield, title: lang === 'fr' ? 'Confidentialit\u00e9' : 'Privacy-first', desc: lang === 'fr' ? 'Vos PDF restent sur votre appareil' : 'Your PDFs stay on your device' },
              ].map((adv, i) => (
                <div key={i} className="p-5 rounded-xl bg-blue-500/5 border border-blue-500/10">
                  <adv.icon className="w-5 h-5 text-blue-400 mb-3" />
                  <h4 className="text-foreground font-medium text-sm mb-1">{adv.title}</h4>
                  <p className="text-muted-foreground text-xs">{adv.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center">
              <h3 className="font-heading text-2xl font-semibold text-foreground mb-3">{labels.ctaTitle}</h3>
              <p className="text-muted-foreground text-sm mb-6">{labels.ctaText}</p>
              <div className="max-w-md mx-auto">
                <WaitlistForm />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
